/**
 * ReportClosingTail — legal disclaimer and theme footer. Renders immediately
 * after the blind-spots appendix so the closing blocks flow without a page gap.
 */
import React from 'react';
import type { CSSProperties } from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import {
  getThemeReaderMode,
  instrumentSurface,
  roleColor,
} from '../../../lib/reportRoleColors';
import type { ReportConflictNotice } from '../../../lib/reportConflictNotice';
import { EditorialSectionShell } from './EditorialSectionShell';
import { ReportLegalDisclaimer } from './ReportLegalDisclaimer';
import { ReportFooter } from '../ReportFooter';
import {
  CLOSING_TAIL_BASE_FLEX_ORDER,
  resolveLegalDisclaimerTailStyle,
  resolveThemeFooterTailStyle,
} from '../../../lib/resolveClosingTailFlexOrder';
import { REPORT_LEGAL_DISCLAIMER_PARAGRAPHS } from '../../../lib/reportLegalDisclaimer';
import { openAuditDisclosureSentence } from '../../../lib/audit/openAuditFlag';
import type { OpenAuditFindings } from '../../../lib/audit/openAuditFlag';
import type { DemonstrationDisclosureV1 } from '../../../types/demonstration';

export interface ReportClosingTailProps {
  theme: ReportTheme;
  sectionOrderFlatten: boolean;
  omitSectionSidebar: boolean;
  /** Pre-resolved unresolved-conflict notice; forwarded to the disclaimer. */
  conflictNotice?: ReportConflictNotice | null;
  demonstrationDisclosure?: DemonstrationDisclosureV1;
  /**
   * AUDIT-ADVISORY (2026-08-22): the open quality-audit resolution for this
   * report (hold stamp / reviewer needs-revision / blocking truth violations).
   * The closing tail travels into the printed PDF and every export, so this is
   * the disclosure that keeps a held report's PDF from reading as a clean
   * deliverable while its DOCX sibling carries the amber banner. 0 / undefined
   * renders nothing.
   *
   * MUST be `openAuditFindings(report)` — the one resolution the page banner,
   * the DOCX cover and the deck's governance table also read. A count derived
   * here would be the second-count defect `openAuditFlag` exists to close.
   *
   * THE RESOLVED PAIR, NEVER A BARE NUMBER (round 8, 2026-08-25). This prop
   * used to accept `number` as well, and `SiteRagReportView` passed
   * `openAuditFindings(report).count` into it. The numeric arm had to guess a
   * `kind` and guessed `'truth-disagreement'` — right on every report whose
   * flag came from a finding that still stands, and WRONG on the floor-of-1
   * `'flagged'` report, where the reader and the printed PDF read "1 quality
   * finding is open on this analysis" while the counsel DOCX and the deck said
   * the analysis is flagged with no individual finding standing. Same report,
   * same export session, opposite claims. Dropping the numeric arm is what
   * makes that state unrepresentable here rather than merely documented: the
   * caller cannot throw the noun away and still compile.
   *
   * The prop was renamed with the shape — `auditRecommendedFindingCount` named
   * a number that is no longer accepted.
   */
  audit?: Pick<OpenAuditFindings, 'count' | 'kind'>;
}

export const ReportClosingTail: React.FC<ReportClosingTailProps> = ({
  theme,
  sectionOrderFlatten,
  omitSectionSidebar,
  conflictNotice,
  demonstrationDisclosure,
  audit,
}) => {
  /*
   * ONE builder, shared with `buildSiteReportDocx`'s counsel cover and the
   * deck's governance table (openAuditFlag.openAuditDisclosureSentence).
   *
   * This block used to compose the sentence inline and end it "Review them in
   * the platform before relying on this document." That was false on the two
   * audiences this DOM actually serves. The reader + the paged/print DOM are
   * the SAME nodes (SiteRagReportView renders this once for both), so the
   * sentence lands in the printed PDF, whose recipient has no platform to open
   * — and a recipient who did open it reaches a surface that itemises none of
   * these findings (the destination audit on `openAuditListNote`). Round 5
   * corrected the wording inside `openAuditListNote` and declared all four
   * surfaces fixed; this emitter never called it and kept printing the old
   * pointer verbatim on the PDF and the web reader.
   *
   * Document wording is used unconditionally rather than branching on
   * print-vs-reader: there is no branch to hang it on, and it is the honest
   * superset — the reader still has the header's "Review quality details"
   * button in the chrome above (`print:hidden`; it replaced DraftBanner's CTA
   * when report pages lost their banners on 2026-10-13), which is where an
   * in-app action belongs.
   */
  const auditDisclosure = openAuditDisclosureSentence(
    // No prop ⇒ nothing resolved ⇒ nothing disclosed. The builder returns null
    // at count 0, so the absent case and the clean-report case take the same
    // path and neither can invent a notice.
    audit ?? { count: 0, kind: 'flagged' },
  );

  const wrapperStyle: CSSProperties = {
    // DARK READER: `theme.backgroundColor` is the RAW light-mode paper token
    // (#ffffff). Every other block picks up the dark page surface, so reading it
    // directly left the closing tail — the IMPORTANT NOTICE disclaimer + footer —
    // as a white slab at the bottom of an otherwise dark report. Light mode is
    // byte-identical (the token is used unchanged); only the dark reader swaps in
    // the instrument surface the rest of the dark ramp is derived against.
    backgroundColor: getThemeReaderMode(theme) === 'dark'
      ? instrumentSurface(theme)
      : theme.backgroundColor,
    breakBefore: 'page',
    pageBreakBefore: 'always',
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
    ...(sectionOrderFlatten ? { order: CLOSING_TAIL_BASE_FLEX_ORDER } : {}),
  };

  return (
    <div data-testid="report-closing-tail" style={wrapperStyle}>
      <EditorialSectionShell
        id="legal-disclaimer"
        data-section="legalDisclaimer"
        data-section-id="legalDisclaimer"
        data-legal-paragraph-count={REPORT_LEGAL_DISCLAIMER_PARAGRAPHS.length}
        style={sectionOrderFlatten ? {} : resolveLegalDisclaimerTailStyle(sectionOrderFlatten)}
        omitSectionSidebar={omitSectionSidebar}
        // SEC-INDENT — defer the gutter to the padded page wrapper in
        // paged/export mode so the closing blocks left-align with the
        // body instead of self-padding an extra 60px.
        shellPadding={sectionOrderFlatten}
        theme={theme}
        extraClassName="pt-6 border-t border-slate-100"
      >
        {auditDisclosure ? (
          <p
            data-testid="report-tail-audit-recommended"
            style={{
              fontSize: 11,
              lineHeight: 1.5,
              marginBottom: 10,
              color: roleColor(theme, 'warning'),
            }}
          >
            {auditDisclosure}
          </p>
        ) : null}
        <ReportLegalDisclaimer embedded theme={theme} conflictNotice={conflictNotice} />
      </EditorialSectionShell>

      <EditorialSectionShell
        omitSectionSidebar={omitSectionSidebar}
        // SEC-INDENT — see legal-disclaimer shell above.
        shellPadding={sectionOrderFlatten}
        theme={theme}
        style={sectionOrderFlatten ? {} : resolveThemeFooterTailStyle(sectionOrderFlatten)}
        extraClassName="pb-2"
      >
        <ReportFooter theme={theme} demonstrationDisclosure={demonstrationDisclosure} />
      </EditorialSectionShell>
    </div>
  );
};
